import { cn } from "~/lib/utils";
import SimpleTooltip from "@components/atoms/SimpleTooltip";
import Spinner from "@components/atoms/Spinner";

type ContainerProps = Pick<React.HTMLAttributes<HTMLDivElement>, "className">;

interface LabelProps extends Pick<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  "className"
> {
  text: string;
}

interface Props extends Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  | "type"
  | "placeholder"
  | "maxLength"
  | "defaultValue"
  | "className"
  | "value"
  | "onChange"
  | "disabled"
  | "inputMode"
  | "pattern"
  | "autoComplete"
> {
  /** Name for the input. The value will be keyed to this name in the form data. */
  id: string;
  errorMessage?: string | null;
  container?: ContainerProps;
  label?: LabelProps;
  labelFontBold?: boolean;
  labelTextColor?: string;
  helpContent?: React.ReactNode;
  helpContentSize?: {
    width: number;
    height: number;
  };
  dataTestId?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  ariaDescribedBy?: string;
  loading?: boolean;
}

export default function Input({
  id,
  errorMessage,
  type = "text",
  placeholder,
  maxLength,
  inputMode,
  defaultValue,
  className,
  value,
  onChange,
  disabled = false,
  container,
  label,
  labelFontBold = true,
  labelTextColor = "text-darkGrey",
  helpContent,
  helpContentSize = { width: 20, height: 20 },
  dataTestId = "input",
  inputRef,
  ariaDescribedBy,
  loading = false,
  autoComplete = "",
  pattern = undefined,
}: Props) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 relative w-full",
        container?.className,
      )}
    >
      <div className="flex">
        {label && (
          <label
            htmlFor={id}
            className={cn(
              `bodySmall ${labelTextColor}`,
              {
                "font-bold": labelFontBold,
              },
              label.className,
            )}
            data-testid="input-label"
          >
            {label.text}
          </label>
        )}

        {helpContent && (
          <SimpleTooltip
            tooltipContent={helpContent}
            width={helpContentSize.width}
            height={helpContentSize.height}
            id={`${id}-help`}
          />
        )}
      </div>

      <div className="relative">
        <input
          id={id}
          name={id}
          type={type}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          defaultValue={defaultValue}
          disabled={disabled}
          value={value}
          onChange={onChange}
          aria-describedby={helpContent ? `${id}-help` : ariaDescribedBy}
          className={cn(
            "bodyDefault border rounded-full px-3 py-2.5 w-full",
            {
              "border-darkError": errorMessage,
              "border-neutralGray": !errorMessage,
              "bg-neutral-200 text-neutral-400 cursor-not-allowed": disabled,
            },
            className,
          )}
          data-testid={dataTestId}
          ref={inputRef}
          autoComplete={autoComplete}
          pattern={pattern}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Spinner size="small" />
          </div>
        )}
      </div>

      {errorMessage && (
        <p className="bodySmall text-darkError" data-testid="input-error">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
